/**
 * Product Gallery Grid - Stacked Layout with Show More
 * Handles desktop grid layout, show more functionality, and mobile slider
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    breakpoint: 1000,
    batchSize: 17,
    animationDuration: 400,
    selectors: {
      gallery: '.product-gallery--stacked',
      grid: '.product-gallery__grid',
      gridItem: '.product-gallery__grid-item',
      showMoreBtn: '[data-action="show-more-media"]',
      showMoreWrapper: '.product-gallery__show-more-wrapper',
      mobileSlider: '.product-gallery__mobile-slider',
      sliderTrack: '.product-gallery__slider-track',
      sliderDots: '.product-gallery__slider-dots',
      slide: '.product-gallery__slide'
    }
  };

  class ProductGalleryGrid {
    constructor(element) {
      this.element = element;
      this.grid = element.querySelector(CONFIG.selectors.grid);
      this.showMoreBtn = element.querySelector(CONFIG.selectors.showMoreBtn);
      this.showMoreWrapper = element.querySelector(CONFIG.selectors.showMoreWrapper);
      this.mobileSlider = element.querySelector(CONFIG.selectors.mobileSlider);

      this.batchSize = parseInt(this.showMoreBtn?.dataset.batchSize || CONFIG.batchSize);

      this.mobileSliderInstance = null;
      this.currentSlideIndex = 0;

      this._init();
    }

    _init() {
      this._attachListeners();
      this._initMobileSlider();
      this._handleResponsive();
      this._initVideoLoading();
    }

    _initVideoLoading() {
      console.log('[ProductGallery] _initVideoLoading called');
      let videosShown = false;

      const showVideos = () => {
        if (videosShown) return; // Prevent multiple calls
        videosShown = true;

        const videos = this.element.querySelectorAll('.product-gallery__media--loading');
        console.log('[ProductGallery] showVideos called, found videos:', videos.length);
        videos.forEach(video => {
          video.classList.remove('product-gallery__media--loading');
          video.classList.add('product-gallery__media--loaded');
        });
      };

      // Find the first image with a src attribute (eager-loaded first image)
      const firstImage = this.element.querySelector('.product-gallery__image[src]');
      console.log('[ProductGallery] firstImage:', firstImage, 'complete:', firstImage?.complete, 'naturalWidth:', firstImage?.naturalWidth);

      if (!firstImage) {
        // No image with src found, show videos immediately
        console.log('[ProductGallery] No firstImage, showing videos immediately');
        showVideos();
        return;
      }

      // Check if image is already loaded
      if (firstImage.complete && firstImage.naturalWidth > 0) {
        console.log('[ProductGallery] Image already loaded, showing videos');
        showVideos();
        return;
      }

      console.log('[ProductGallery] Waiting for image to load...');

      // Wait for image to load
      firstImage.addEventListener('load', showVideos, { once: true });
      firstImage.addEventListener('error', showVideos, { once: true });

      // Fallback: show videos after 1 second max
      setTimeout(showVideos, 1000);
    }

    _attachListeners() {
      // Show more button
      if (this.showMoreBtn) {
        this.showMoreBtn.addEventListener('click', this._onShowMore.bind(this));
      }

      // Variant change listener (Shopify theme event)
      document.addEventListener('variant:changed', this._onVariantChanged.bind(this));

      // Resize handler
      window.addEventListener('resize', this._debounce(this._handleResponsive.bind(this), 150));
    }

    _onShowMore(event) {
      event.preventDefault();

      if (!this.grid) return;

      const hiddenItems = this.grid.querySelectorAll('.product-gallery__grid-item--hidden');
      const itemsToShow = Array.from(hiddenItems).slice(0, this.batchSize);

      // Reveal items with staggered animation
      itemsToShow.forEach((item, index) => {
        setTimeout(() => {
          item.classList.remove('product-gallery__grid-item--hidden');
          item.classList.add('product-gallery__grid-item--revealing');

          // Remove animation class after completion
          setTimeout(() => {
            item.classList.remove('product-gallery__grid-item--revealing');
          }, CONFIG.animationDuration);
        }, index * 50); // Stagger by 50ms
      });

      // Update counter after animation starts
      setTimeout(() => {
        this._updateShowMoreButton();

        // Trigger lazysizes to load newly visible images
        if (window.lazySizes) {
          window.lazySizes.autoSizer.checkElems();
        }
      }, itemsToShow.length * 50);
    }

    _updateShowMoreButton() {
      if (!this.grid || !this.showMoreWrapper) return;

      const remainingHidden = this.grid.querySelectorAll('.product-gallery__grid-item--hidden').length;

      if (remainingHidden === 0) {
        this.showMoreWrapper.classList.add('product-gallery__show-more-wrapper--hidden');
      }
    }

    _onVariantChanged(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      // Reorder media based on variant's featured image
      this._reorderMediaByVariant(variant);

      // Reset show more state
      this._resetShowMore();

      // Update mobile slider
      this._updateMobileSlider();
    }

    _reorderMediaByVariant(variant) {
      if (!this.grid) return;

      const gridItems = Array.from(this.grid.querySelectorAll(CONFIG.selectors.gridItem));
      const featuredMediaId = variant?.featured_media?.id;

      // 1. Sort by original index (restore Shopify order)
      gridItems.sort((a, b) =>
        parseInt(a.dataset.originalIndex) - parseInt(b.dataset.originalIndex)
      );

      // 2. Move variant's featured image to first position (if it exists)
      if (featuredMediaId) {
        const featuredIndex = gridItems.findIndex(
          item => parseInt(item.dataset.mediaId) === featuredMediaId
        );
        if (featuredIndex > 0) {
          const [featuredItem] = gridItems.splice(featuredIndex, 1);
          gridItems.unshift(featuredItem);
        }
      }

      // 3. Re-append to DOM in new order + update full/half classes
      gridItems.forEach((item, index) => {
        item.classList.toggle('product-gallery__grid-item--full', index === 0);
        item.classList.toggle('product-gallery__grid-item--half', index !== 0);
        this.grid.appendChild(item);
      });

      // 4. Move 3D buttons to the new first item (they should always stay on image0)
      const buttonsContainer = this.element.querySelector('#mazing-buttons');
      if (buttonsContainer && gridItems[0]) {
        gridItems[0].appendChild(buttonsContainer);
      }

      // 5. Reorder mobile slider to match
      this._reorderMobileSlider(gridItems);

      // 6. Trigger lazysizes to load newly visible images
      if (window.lazySizes) {
        window.lazySizes.autoSizer.checkElems();
      }
    }

    _reorderMobileSlider(gridOrder) {
      if (!this.mobileSlider) return;
      const track = this.mobileSlider.querySelector(CONFIG.selectors.sliderTrack);
      if (!track) return;

      gridOrder.forEach(gridItem => {
        const mediaId = gridItem.dataset.mediaId;
        const slide = track.querySelector(`[data-media-id="${mediaId}"]`);
        if (slide) track.appendChild(slide);
      });
    }

    _resetShowMore() {
      if (!this.grid) return;

      const allItems = this.grid.querySelectorAll(CONFIG.selectors.gridItem);

      allItems.forEach((item, index) => {
        if (index < this.batchSize) {
          item.classList.remove('product-gallery__grid-item--hidden');
        } else {
          item.classList.add('product-gallery__grid-item--hidden');
        }
      });

      this._updateShowMoreButton();

      // Show the button wrapper if there are hidden items
      if (this.showMoreWrapper) {
        const hasHidden = this.grid.querySelectorAll('.product-gallery__grid-item--hidden').length > 0;
        this.showMoreWrapper.classList.toggle('product-gallery__show-more-wrapper--hidden', !hasHidden);
      }
    }

    /**
     * Mobile Slider Implementation
     */
    _initMobileSlider() {
      if (!this.mobileSlider || window.innerWidth >= CONFIG.breakpoint) return;

      const track = this.mobileSlider.querySelector(CONFIG.selectors.sliderTrack);
      if (!track) return;

      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      let wasSwipe = false;

      const getVisibleSlides = () => {
        return Array.from(this.mobileSlider.querySelectorAll(CONFIG.selectors.slide));
      };

      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        currentX = startX;
        isDragging = true;
        track.style.transition = 'none';
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
        const diff = currentX - startX;
        const currentOffset = -this.currentSlideIndex * 100;
        track.style.transform = `translateX(calc(${currentOffset}% + ${diff}px))`;
      }, { passive: true });

      track.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = 'transform 0.3s ease-out';

        const diff = currentX - startX;
        const threshold = 50;
        const slides = getVisibleSlides();
        const maxIndex = slides.length - 1;

        // Mark as swipe if movement exceeded small threshold (prevents lightbox on swipe)
        wasSwipe = Math.abs(diff) > 10;

        if (diff < -threshold && this.currentSlideIndex < maxIndex) {
          this.currentSlideIndex++;
        } else if (diff > threshold && this.currentSlideIndex > 0) {
          this.currentSlideIndex--;
        }

        this._goToSlide(this.currentSlideIndex);
        this._updateDots();
      });

      // Prevent lightbox click if it was a swipe gesture
      track.addEventListener('click', (e) => {
        if (wasSwipe) {
          e.preventDefault();
          e.stopPropagation();
          wasSwipe = false;
        }
      }, true);

      // Reset mobile slider to first image when lightbox closes
      const self = this;
      const sliderContainer = this.mobileSlider;
      const resetSlider = function() {
        if (window.innerWidth >= CONFIG.breakpoint) return;

        self.currentSlideIndex = 0;
        track.style.transition = 'transform 0.3s ease-out';
        track.style.transform = 'translateX(0%)';
        sliderContainer.scrollLeft = 0;
        self._updateDots();
      };

      // Watch for FancyBox close using MutationObserver
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.removedNodes.forEach(function(node) {
            if (node.classList && node.classList.contains('fancybox-container')) {
              resetSlider();
            }
          });
        });
      });
      observer.observe(document.body, { childList: true });

      // Create pagination dots
      this._createDots();

      this.mobileSliderInstance = { track, getVisibleSlides };
    }

    _createDots() {
      if (!this.mobileSlider) return;

      const slides = this.mobileSlider.querySelectorAll(CONFIG.selectors.slide);
      if (slides.length <= 1) return;

      // Check if dots container already exists
      let dotsContainer = this.mobileSlider.querySelector(CONFIG.selectors.sliderDots);
      if (!dotsContainer) {
        dotsContainer = document.createElement('div');
        dotsContainer.className = 'product-gallery__slider-dots';
        this.mobileSlider.appendChild(dotsContainer);
      }

      dotsContainer.innerHTML = '';

      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'product-gallery__slider-dot' + (index === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => {
          this.currentSlideIndex = index;
          this._goToSlide(index);
          this._updateDots();
        });
        dotsContainer.appendChild(dot);
      });
    }

    _updateDots() {
      if (!this.mobileSlider) return;

      const dots = this.mobileSlider.querySelectorAll('.product-gallery__slider-dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentSlideIndex);
      });
    }

    _goToSlide(index) {
      if (!this.mobileSliderInstance) return;

      const track = this.mobileSliderInstance.track;
      track.style.transform = `translateX(-${index * 100}%)`;
      this.currentSlideIndex = index;
    }

    _updateMobileSlider() {
      this.currentSlideIndex = 0;
      this._goToSlide(0);
      this._createDots();
    }

    _handleResponsive() {
      const isDesktop = window.innerWidth >= CONFIG.breakpoint;

      if (isDesktop) {
        // Ensure grid is visible, slider hidden (CSS handles this, but just in case)
        if (this.grid) this.grid.style.display = '';
        if (this.mobileSlider) this.mobileSlider.style.display = 'none';
      } else {
        // Ensure slider is visible, grid hidden
        if (this.grid) this.grid.style.display = 'none';
        if (this.mobileSlider) this.mobileSlider.style.display = '';

        // Initialize slider if not already
        if (!this.mobileSliderInstance) {
          this._initMobileSlider();
        }
      }
    }

    _debounce(fn, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    destroy() {
      // Cleanup if needed
    }
  }

  // Initialize on DOM ready
  function init() {
    const galleries = document.querySelectorAll(CONFIG.selectors.gallery);
    galleries.forEach(gallery => {
      if (!gallery.dataset.gridInitialized) {
        new ProductGalleryGrid(gallery);
        gallery.dataset.gridInitialized = 'true';
      }
    });
  }

  // Support for section rendering (Shopify AJAX section loading)
  document.addEventListener('shopify:section:load', init);

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for external use
  window.ProductGalleryGrid = ProductGalleryGrid;
})();
